import { useCallback, useRef } from "react";
import { useForm } from "@mantine/form";
import { Editor } from "@tiptap/react";
import {
  Input,
  NativeSelect,
  NumberInput,
  TextInput
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import ContentEditor from "../ContentEditor";
import {
  DesignProjectFields,
  NewRequestFormProps
} from "../../types/request.types";
import FormAttachments from "../attachments/FormAttachments";
import FormContacts from "../contacts/FormContacts";
import { useConvertFrom } from "../../hooks/use-convert-from";
import { ActionBar } from "./ActionBar";

export default function DesignProjectForm(props: NewRequestFormProps) {
  const { onSubmit, tempId, fromRequest } = props;
  const editorRef = useRef<Editor>(null);
  const form = useForm<DesignProjectFields>({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      project_type: null,
      history: 'new',
      content: '',
      quantity: null,
      budget_code: '',
      requested_completion_date: null,
    },
    validate: {
      content: val => (
        editorRef.current && editorRef.current.getText().length > 0 ?
          null : 'Please fill out this field'
      ),
      requested_completion_date: val => (
        val ? null : 'Please fill out this field'
      )
    }
  });
  useConvertFrom<DesignProjectFields>(form, editorRef.current, fromRequest);

  const onFormSubmit = useCallback((submitVals: DesignProjectFields) => {
    const newRequest = { ...submitVals };
    newRequest.content = editorRef.current?.getHTML();
    onSubmit(newRequest);
  }, []);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      {!fromRequest && (
        <Input.Wrapper
          label="Contacts"
          mt="md"
        >
          <FormContacts<DesignProjectFields> form={form} />
        </Input.Wrapper>
      )}

      <TextInput
        {...form.getInputProps('name')}
        key={form.key('name')}
        label="Project name"
        mt="md"
        required
      />
      
      <NativeSelect
        {...form.getInputProps('project_type')}
        key={form.key('project_type')}
        label="Type"
        mt="md"
        data={[
          { value: '', label: '' },
          { value: 'poster', label: 'Poster' },
          { value: 'flyer', label: 'Flyer' },
          { value: 'banner', label: 'Banner' },
          { value: 'brochure', label: 'Brochure' },
          { value: 'booklet', label: 'Booklet' },
          { value: 'mailing', label: 'Mailing' },
          { value: 'invitation', label: 'Invitation' },
          { value: 'postcard', label: 'Postcard' },
          { value: 'other', label: 'Other' },
        ]}
      />

      <NativeSelect
        {...form.getInputProps('history')}
        key={form.key('history')}
        label="History"
        mt="md"
        data={[
          { value: 'new', label: 'New' },
          { value: 'reprint with changes', label: 'Reprint with changes' },
          { value: 'reprint with no changes', label: 'Reprint with NO changes' },
        ]}
      />

      <ContentEditor<DesignProjectFields>
        formKey="content"
        label="Description"
        required={true}
        form={form} 
        editorRef={editorRef}
        mt="md"
      />

      <NumberInput
        {...form.getInputProps('quantity')}
        key={form.key('quantity')}
        label="Quantity"
        min={1}
        mt="md"
      />

      <TextInput
        {...form.getInputProps('budget_code')}
        key={form.key('budget_code')}
        label="Budget code"
        mt="md"
      />

      <DatePickerInput
        {...form.getInputProps('requested_completion_date')}
        key={form.key('requested_completion_date')}
        label="Requested completion date"
        required
        highlightToday
        mt="md"
      />

      {!fromRequest && (
        <Input.Wrapper
          label="Attachments"
          mt="md"
        >
          <FormAttachments tempId={tempId} />
        </Input.Wrapper>
      )}

      <ActionBar showConvertNotice={!!fromRequest} />
    </form>
  );
}
